import React, { useMemo, useState } from 'react';
import Icon from '../../../Icon/Icon';
import CommonButton from '../../../Buttons/CommonButton';
import Hint from '../../../Tools/Hint';
import { formatNumber } from '../../../../utils/functions.js';
import { createTierPricing } from '../../../../services/pricingService';
import { useDispatch } from 'react-redux';
import { notificationsActions } from '../../../../store';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

const CreateTier = ({
    isActive,
    rerender,
    screenPrices,
    currenciesWithLabel,
    selectedCurrency,
    handleChangeCurrency,
    venueTypes = [],
    onTierLogCreated,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [tier, setTier] = useState('');
    const [pricing, setPricing] = useState('');
    const [selectedVenueType, setSelectedVenueType] = useState(null);
    const [additionalNotes, setAdditionalNotes] = useState('');

    const inputPlaceholder = formatNumber('0.00', 'properCurrency', null, {
        currency: 'USD',
    });

    const venueTypeOptions = useMemo(
        () =>
            (venueTypes || []).map((venue) => ({
                value: venue.id,
                label: venue.name,
                description: venue.description,
            })),
        [venueTypes]
    );

    const formatVenueTypeOption = (option) => {
        if (!option) return null;
        return (
            <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">
                    {option.label}
                </span>
                {option.description && (
                    <span className="text-xs text-gray-500">
                        {option.description}
                    </span>
                )}
            </div>
        );
    };

    const handleChangeTier = (e) => {
        const tier = e.target.value;
        const formatedTier = !isNaN(parseInt(formatNumber(tier, 'nomarker')))
            ? parseInt(formatNumber(tier, 'nomarker'))
            : 0;

        formatedTier === 0 ? setTier('') : setTier(formatedTier);
    };

    const handleCreatePrice = () => {
        if (!selectedVenueType) return;
        const tierValue = parseInt(tier, 10);
        const baseCpmValue = parseFloat(pricing);
        const notes = additionalNotes.trim();
        if (Number.isNaN(tierValue) || Number.isNaN(baseCpmValue)) return;
        const bodyReq = {
            tier: tierValue,
            screenType: selectedVenueType.label,
            venueTypeId: selectedVenueType.value,
            baseCpm: baseCpmValue,
            notes: notes || undefined,
        };

        createTierPricing(bodyReq).then((res) => {
            if (res.statusCode === 200) {
                setTier('');
                setPricing('');
                setSelectedVenueType(null);
                setAdditionalNotes('');
                if (onTierLogCreated) onTierLogCreated();
                dispatch(
                    notificationsActions.setNotification({
                        type: 'success',
                        title: t(
                            'sections.management.section.pricing.createTier.notificationCreated.title'
                        ),
                        message: t(
                            'sections.management.section.pricing.createTier.notificationCreated.message'
                        ),
                    })
                );
            } else {
                dispatch(
                    notificationsActions.setNotification({
                        type: 'error',
                        title: t(
                            'sections.management.section.pricing.createTier.notificationCreated.errorTitle'
                        ),
                        message: res.message,
                    })
                );
            }
        });
    };

    const handleChangePricing = (e) => {
        const price = e.target.value;
        if (price === "") {
            setPricing(price);
            return;
        }

        if (!isNaN(price) && Number(price) > 0) {
            setPricing(price);
        }
    };

    const handleVenueTypeChange = (option) => {
        setSelectedVenueType(option);
    };

    const handleNotesChange = (event) => {
        setAdditionalNotes(event.target.value);
    };

    return (
        <div className={`${isActive ? 'block' : 'hidden'} px-4 w-full`}>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                <section className="bg-white bg-opacity-90 rounded-2xl shadow-sm p-6 xl:col-span-2 space-y-6">
                    <div className="flex items-start justify-between border-b border-gray-100 pb-4">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-gray-400">
                                {t('sections.management.section.pricing.createTier.tab')}
                            </p>
                            <h2 className="text-lg font-semibold text-gray-800">
                                {t('sections.management.section.pricing.createTier.title')}
                            </h2>
                        </div>
                        <Icon
                            name="chart-bar"
                            className="text-blue-400 text-2xl"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label
                                htmlFor="tierid"
                                className="block text-sm font-semibold text-gray-700">
                                {t(
                                    'sections.management.section.pricing.tierList.columns.tierName'
                                )}
                            </label>
                            <p className="text-xs text-gray-400">
                                {t(
                                    'sections.management.section.pricing.createTier.placeholder'
                                )}
                            </p>
                            <input
                                type="text"
                                id="tierid"
                                data-testid="tierid"
                                value={tier}
                                autoComplete="off"
                                onChange={handleChangeTier}
                                className="mt-2 focus:ring-2 focus:ring-blue-200 focus:outline-none block w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder={t('sections.management.section.pricing.createTier.placeholder')}
                            />
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <label
                                    htmlFor="tierprice"
                                    className="block text-sm font-semibold text-gray-700">
                                    {t('sections.management.section.pricing.createTier.setPrice.title')}
                                </label>
                                <Hint
                                    className="text-gray-400 text-sm"
                                    title={t(
                                        'sections.management.section.pricing.common.hint.title'
                                    )}
                                    text={t(
                                        'sections.management.section.pricing.common.hint.description'
                                    )}
                                    type={'info'}
                                    dataFor="price"
                                />
                            </div>
                            <p className="text-xs text-gray-400">
                                {t(
                                    'sections.management.section.pricing.createTier.setPrice.label'
                                )}
                            </p>
                            <input
                                type="number"
                                steps="0.01"
                                min="0"
                                id="tierprice"
                                data-testid="tierprice"
                                value={pricing}
                                autoComplete="off"
                                onChange={handleChangePricing}
                                className="mt-2 focus:ring-2 focus:ring-blue-200 focus:outline-none block w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                                placeholder={inputPlaceholder}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label
                            htmlFor="venueType"
                            className="flex items-center text-sm font-semibold text-gray-700">
                            <Icon
                                name="sign"
                                className="text-blue-400 mr-2"
                            />
                            {t('sections.management.section.pricing.createTier.screenType.title')}
                        </label>
                        <p className="text-xs text-gray-400">
                            {t('sections.management.section.pricing.createTier.screenType.label')}
                        </p>
                        <Select
                            inputId="venueType"
                            placeholder={t(
                                'sections.management.section.pricing.createTier.screenType.label'
                            )}
                            options={venueTypeOptions}
                            value={selectedVenueType}
                            onChange={handleVenueTypeChange}
                            isDisabled={!venueTypeOptions.length}
                            noOptionsMessage={() =>
                                t(
                                    'sections.management.section.pricing.createTier.screenType.empty'
                                )
                            }
                            formatOptionLabel={formatVenueTypeOption}
                            classNamePrefix="create-tier-select"
                        />
                    </div>

                    <div className="space-y-2">
                        <label
                            htmlFor="tier-additional-notes"
                            className="flex items-center text-sm font-semibold text-gray-700">
                            <Icon
                                name="comment-dots"
                                className="text-blue-400 mr-2"
                            />
                            {t('sections.management.section.pricing.createTier.notes.title')}
                            <span className="ml-2 text-xs font-normal text-gray-400">
                                {t('common.labels.optional', {
                                    defaultValue: 'Optional',
                                })}
                            </span>
                        </label>
                        <p className="text-xs text-gray-400">
                            {t('sections.management.section.pricing.createTier.notes.label')}
                        </p>
                        <textarea
                            id="tier-additional-notes"
                            data-testid="tier-additional-notes"
                            value={additionalNotes}
                            onChange={handleNotesChange}
                            className="focus:ring-2 focus:ring-blue-200 focus:outline-none block w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                            rows={3}
                            placeholder={t(
                                'sections.management.section.pricing.createTier.notes.placeholder'
                            )}
                        />
                    </div>

                    <div className="flex justify-end pt-2">
                        <CommonButton
                            className="mt-4"
                            label={t(
                                'sections.management.section.pricing.createTier.tab'
                            )}
                            styleType="primary"
                            disabled={tier === '' || pricing === '' || !selectedVenueType}
                            onClick={handleCreatePrice}
                            id="create-tier-btn"
                        />
                    </div>
                </section>

                <section className="bg-white bg-opacity-90 rounded-2xl shadow-sm p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-gray-400">
                                {t(
                                    'sections.management.section.pricing.common.currency.title'
                                )}
                            </p>
                            <p className="text-sm text-gray-500">
                                {t(
                                    'sections.management.section.pricing.common.currency.label'
                                )}
                            </p>
                        </div>
                        <Icon
                            name="coin"
                            className="text-blue-400 text-xl"
                        />
                    </div>
                    <Select
                        inputId="currency"
                        placeholder={t(
                            'sections.management.section.billing.funding.currencySelect'
                        )}
                        options={currenciesWithLabel}
                        value={selectedCurrency}
                        onChange={handleChangeCurrency}
                        id="select-currency"
                        classNamePrefix="create-tier-select"
                    />
                    {selectedCurrency && screenPrices && pricing !== '' && (
                        <div className="rounded-lg bg-blue-50 text-blue-700 px-4 py-3 text-sm">
                            {t(
                                'sections.management.section.pricing.common.price.title'
                            )}{' '}
                            {selectedCurrency?.value}:{' '}
                            {formatNumber(
                                pricing * (screenPrices[selectedCurrency?.value] || 1),
                                'properCurrency',
                                null,
                                {
                                    currency: selectedCurrency?.value,
                                }
                            )}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default CreateTier;
